import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Avatar, Box, Flex, Text, VStack } from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import type { WalletPendingPayoutItem } from '@foodfluence/shared';
import { BountyDataContext } from '../../../contexts/BountyDataContext';
import { CollabDataContext } from '../../../contexts/collabDataContext';
import { InfluencerDataContext } from '../../../contexts/InfluencerDataContext';
import { InfluencerCampaignDataContext } from '../../../contexts/InfluencerCampaignDataContext';
import { ClientDataContext } from '../../../contexts/ClientDataContext';
import { walletApi } from '../../../services/api/wallet';
import { ff } from '../../../theme';
import { walletColors } from '../walletTheme';
import { buildWalletActivity, enrichActivityWithPayoutStatus, formatActivityDate } from '../utils/walletUtils';

const listCardSx = {
  bg: 'white',
  border: '1px solid',
  borderColor: walletColors.cardBorder,
  borderRadius: '20px',
  overflow: 'hidden',
} as const;

export interface WalletRecentActivityProps {
  pendingItems?: WalletPendingPayoutItem[];
  /** Re-fetch bounty claims after wallet bootstrap sweeps payouts so rows show as paid. */
  payoutSweepAmount?: number;
}

const WalletRecentActivity: React.FC<WalletRecentActivityProps> = ({
  pendingItems = [],
  payoutSweepAmount,
}) => {
  const navigate = useNavigate();
  const collabData = useContext(CollabDataContext);
  const { claims: bountyClaims, bounties, refetchBounties } = useContext(BountyDataContext);
  const { influencerData } = useContext(InfluencerDataContext);
  const campaignData = useContext(InfluencerCampaignDataContext);
  const clients = useContext(ClientDataContext);
  const [lcOrders, setLcOrders] = useState<Awaited<ReturnType<typeof walletApi.getLcOrders>>['orders']>([]);

  useEffect(() => {
    if (!influencerData?.id) {
      setLcOrders([]);
      return;
    }
    walletApi
      .getLcOrders({ influencerId: influencerData.id })
      .then((res) => setLcOrders(res.orders))
      .catch(() => setLcOrders([]));
  }, [influencerData?.id]);

  useEffect(() => {
    if (!influencerData?.id) return;
    refetchBounties();
  }, [influencerData?.id, payoutSweepAmount, refetchBounties]);

  const bountyLogoByBountyId = useMemo(
    () => Object.fromEntries(bounties.map((bounty) => [bounty.id, bounty.logoUrl])),
    [bounties],
  );

  const activities = useMemo(() => {
    const base = buildWalletActivity(
      collabData,
      campaignData,
      clients,
      lcOrders,
      bountyClaims,
      bountyLogoByBountyId,
      5,
    );
    return enrichActivityWithPayoutStatus(base, pendingItems);
  }, [collabData, campaignData, clients, lcOrders, bountyClaims, bountyLogoByBountyId, pendingItems]);

  return (
    <Box fontFamily="display">
      <Text textStyle="display" fontSize="20px" color={walletColors.textPrimary} mb={3} px={1}>
        Recent activity
      </Text>

      <Box {...listCardSx}>
        {activities.length === 0 ? (
          <Box px={4} py={6}>
            <Text fontSize="14px" color={walletColors.textSecondary}>
              Completed collabs, bounties, and payouts will show up here.
            </Text>
          </Box>
        ) : (
          <VStack spacing={0} align="stretch">
            {activities.map((item, index) => {
              const isCollabRow = item.kind === 'collab' && Boolean(item.collabId);
              const isPendingPayout = Boolean(item.payoutStatusLabel);
              const amountColor = isPendingPayout ? walletColors.textSecondary : ff.ink;

              return (
                <Flex
                  key={item.id}
                  align="center"
                  gap={3}
                  px="16px"
                  py="12px"
                  minH="64px"
                  w="100%"
                  borderTop={index > 0 ? '1px solid' : undefined}
                  borderColor={walletColors.cardBorder}
                  cursor={isCollabRow ? 'pointer' : 'default'}
                  transition="background-color 0.15s ease"
                  _hover={isCollabRow ? { bg: ff.grey } : undefined}
                  _active={isCollabRow ? { bg: ff.line } : undefined}
                  role={isCollabRow ? 'button' : undefined}
                  tabIndex={isCollabRow ? 0 : undefined}
                  onClick={
                    isCollabRow ? () => navigate(`/collabs/collab-details/${item.collabId}`) : undefined
                  }
                  onKeyDown={
                    isCollabRow
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/collabs/collab-details/${item.collabId}`);
                          }
                        }
                      : undefined
                  }
                >
                  <Avatar
                    size="sm"
                    name={item.title}
                    src={item.logoUrl}
                    borderRadius="12px"
                    w="38px"
                    h="38px"
                  />
                  <Box flex={1} minW={0}>
                    <Text fontSize="15px" fontWeight="600" color={walletColors.textPrimary} noOfLines={1}>
                      {item.title}
                    </Text>
                    {item.compensationBreakdown ? (
                      <Text fontSize="13px" color={ff.body} noOfLines={2} mt={0.5}>
                        {item.compensationBreakdown}
                      </Text>
                    ) : null}
                    <Text fontSize="13px" color={walletColors.textSecondary} noOfLines={1} mt={0.5}>
                      {item.payoutStatusLabel ?? item.subtitle}
                    </Text>
                  </Box>
                  <Box textAlign="right" flexShrink={0}>
                    {item.amount > 0 ? (
                      <>
                        <Text textStyle="display" fontSize="17px" color={amountColor}>
                          +${item.amount.toFixed(item.amount % 1 === 0 ? 0 : 2)}
                        </Text>
                        {item.showFeeHint !== false ? (
                          <Text fontSize="10px" color={walletColors.textMuted} lineHeight="12px">
                            (Incl. fees)
                          </Text>
                        ) : null}
                      </>
                    ) : null}
                    <Text fontSize="11px" color={walletColors.textMuted} mt={item.amount > 0 ? '2px' : 0}>
                      {formatActivityDate(item.date)}
                    </Text>
                  </Box>
                  {isCollabRow ? (
                    <ChevronRightIcon boxSize={4} color={walletColors.textMuted} flexShrink={0} />
                  ) : null}
                </Flex>
              );
            })}
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default WalletRecentActivity;
